import React, { useState, useEffect } from "react";
import { DUMMY_LEDGERS } from "../data/dummyVoucherData";
import { getFilteredLedgers } from "../data/ledgerFilterRules";

export default function LedgerSearchModal({
  isOpen,
  onClose,
  onSelectLedger,
  currentRowIndex,
  ledgers = [],
  voucherType,
  rowType = "Dr",
  isModifyMode = false,
}) {
  const [query, setQuery] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("all");

  // Keyboard Escape listener to dismiss modal
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const allLedgers = ledgers && ledgers.length > 0 ? ledgers : DUMMY_LEDGERS;

  // Apply voucher-type + Dr/Cr filtering first (in modify mode, allow all ledgers)
  const displayLedgers = isModifyMode
    ? allLedgers
    : getFilteredLedgers(allLedgers, voucherType, rowType);

  const groups = [
    "all",
    ...new Set(
      displayLedgers
        .map((l) => l.group || l.GroupName || l.GroupNAME)
        .filter(Boolean)
    ),
  ];

  const filtered = displayLedgers.filter((l) => {
    const lName = String(l.name || l.LedgerName || "").toLowerCase();
    const lCode = String(l.headCode || l.HEAD_CODE || l.id || "").toLowerCase();
    const lGroup = String(l.group || l.GroupName || l.GroupNAME || "").toLowerCase();
    const q = query.toLowerCase().trim();

    const matchesQuery = !q || lName.includes(q) || lCode.includes(q) || lGroup.includes(q);
    const itemGroup = l.group || l.GroupName || l.GroupNAME;
    const matchesGroup = selectedGroup === "all" || itemGroup === selectedGroup;
    return matchesQuery && matchesGroup;
  });

  return (
    <div
      className="vch_modal_backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="vch_modal_dialog" onClick={(e) => e.stopPropagation()}>
        <div className="vch_modal_header">
          <div className="vch_modal_title_area">
            <i className="fa-solid fa-book-bookmark"></i>
            <h5>Select Ledger Account</h5>
          </div>
          <button
            type="button"
            className="vch_modal_close_btn"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            aria-label="Close"
          >
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div className="vch_modal_body">
          <div className="vch_modal_filter_bar">
            <div className="vch_search_input_wrap">
              <i className="fa-solid fa-magnifying-glass"></i>
              <input
                type="text"
                autoFocus
                placeholder="Search ledger name, account group, code..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>

            <select
              className="vch_group_filter_select"
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value)}
            >
              <option value="all">All Groups</option>
              {groups
                .filter((g) => g !== "all")
                .map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
            </select>
          </div>

          <div className="vch_ledger_list_container">
            <table className="vch_ledger_lookup_table">
              <thead>
                <tr>
                  <th style={{ width: "80px" }}>Code</th>
                  <th>Ledger Name</th>
                  <th>Account Group</th>
                  <th style={{ textAlign: "right", width: "160px" }}>Current Balance</th>
                  <th style={{ width: "90px", textAlign: "center" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="vch_no_ledger_found">
                      No matching ledger account found
                    </td>
                  </tr>
                ) : (
                  filtered.map((item) => {
                    const lName = item.name || item.LedgerName;
                    const lCode = item.headCode || item.HEAD_CODE || item.id;
                    const lGroup = item.group || item.GroupName || item.GroupNAME || "-";
                    const lBal = item.balance !== undefined
                      ? item.balance
                      : `${Number(item.CL_BALANCE || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })} ${item.OpenBlType || item.openType || "Dr"}`;

                    return (
                      <tr
                        key={lCode || lName}
                        className="vch_ledger_lookup_row"
                        onClick={() => {
                          onSelectLedger(lName, lCode);
                          onClose();
                        }}
                      >
                        <td className="vch_ledger_code">{lCode}</td>
                        <td className="vch_ledger_name">
                          <strong>{lName}</strong>
                        </td>
                        <td>
                          <span className="vch_group_pill">{lGroup}</span>
                        </td>
                        <td className="vch_ledger_bal" style={{ textAlign: "right" }}>
                          {lBal}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <button
                            type="button"
                            className="vch_pick_ledger_btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSelectLedger(lName, lCode);
                              onClose();
                            }}
                          >
                            Select
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="vch_modal_footer">
          <span className="vch_modal_hint">
            Tip: Press Esc or click outside to dismiss this modal.
          </span>
          <button
            type="button"
            className="vch_btn_secondary"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
